/**
 * Solicitudes de eliminación: donde las supervisoras aprueban o rechazan
 * (spec 2026-09-24-solicitud-eliminacion). Aprobar ejecuta el borrado de
 * siempre y deja la entrada en la papelera.
 *
 * Grupos (spec 2026-09-28-grupos-supervisoras): filtro "Mi grupo / Todas",
 * columna "Avisado a", etiqueta "Sin grupo", pestaña "Mi grupo" para armar el
 * grupo y la suplencia, y "Previsualizar" (documento que se conserva junto al
 * archivo que se eliminará).
 *
 * Acciones en lote: casillas en Pendientes y barra "Aprobar/Rechazar
 * seleccionadas" (o todas las visibles si no hay selección). Ver bulk_actions.js.
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { Tabs, Table, Button, Modal, Input, notification, Typography, Tag, Space, Alert, Segmented } from 'antd';

import { deletionRequests } from '../../actions/database/deletion_requests';
import { KIND_LABELS, isSafeStorageUrl, needsExecution, friendlyError, kindWithArticle } from '../../lib/deletion_request';
import { useSupervisorDirectory } from './use_directory';
import { useMyCustomerIds } from './use_my_customers';
import { isScoped } from '../../lib/user_scope';
import { subscribeMany } from '../../lib/subscribe_many';
import { MyGroup } from './my_group';
import { PreviewDrawer } from './preview_drawer';
import { BulkActions } from './bulk_actions';
import { keepVisible } from '../../lib/bulk_resolve';
import { myGroupMembers, inMyGroup, isUnassigned, defaultScope, classifyUsers, effectiveBackup } from '../../lib/supervisor_groups';

const fmt = (ts) => {
  const d = ts?.toDate?.();
  return d ? d.toLocaleString('es-CO') : '—';
};
const RESOLVED_PAGE = 50;
const byNewest = (field) => (a, b) => (b[field]?.toMillis?.() || 0) - (a[field]?.toMillis?.() || 0);

// Supervisora de empresa: una consulta por empresa (las reglas solo dejan
// leer las de sus empresas). Global: la consulta de siempre. subscribeMany
// conserva lo que cargó cada consulta aunque otra falle.

/** Número de solicitudes pendientes, en vivo. Para el contador del menú. */
export const usePendingRequestCount = (enabled, roles) => {
  const firebase = useFirebase();
  const scoped = isScoped(roles);
  const mine = useMyCustomerIds(enabled && scoped);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled || (scoped && !mine.ids)) { setCount(0); return undefined; }
    const col = firebase.firestore().collection('deletion_requests').where('status', '==', 'pending');
    const queries = scoped ? mine.ids.map((cid) => col.where('customerId', '==', cid)) : [col];
    // Si una empresa falla, el contador sigue con lo que sí cargó.
    return subscribeMany(queries, ({ docs }) => setCount(docs.length));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scoped, mine.ids]);
  return count;
};

export const DeletionRequests = () => {
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const roles = useSelector((state) => state.get('roles').get('roles').roles);
  const scoped = isScoped(roles);
  const mine = useMyCustomerIds(scoped);
  // Errores separados: que carguen las resueltas no borra el de pendientes.
  const [pendingError, setPendingError] = useState(null);
  const [resolvedError, setResolvedError] = useState(null);
  const [pending, setPending] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);

  const myUid = firebase.auth().currentUser?.uid;
  const isRootUser = roles?.documents?.root === true;
  // Grupos y directorio son de supervisoras globales (filtro "Mi grupo",
  // "Sin grupo", pestaña de grupo). La de empresa no los usa ni los carga.
  const directory = useSupervisorDirectory(!scoped);
  // Supervisoras vigentes, con la misma regla que la función al enrutar. Si no
  // se pudieron leer los usuarios, no se filtra por vigencia (mejor ver de más).
  const supervisorUids = directory.users.length && !directory.error
    ? new Set(classifyUsers(directory.users, directory.rolesById).supervisors.map((u) => u.uid))
    : undefined;
  const members = myGroupMembers({ myUid, groups: directory.groups, supervisorUids });
  const [scope, setScope] = useState(null);
  // El filtro arranca en "Mi grupo" si ella tiene a alguien; se decide una vez,
  // cuando llegan los grupos, y después lo cambia ella.
  useEffect(() => {
    if (scope === null && !directory.loading) setScope(defaultScope(members));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directory.loading, directory.groups]);
  const inScope = (r) => scoped || scope !== 'mine' || inMyGroup(r, members);
  const myGroupDoc = directory.groups.find((g) => g.uid === myUid);
  const myBackup = myGroupDoc ? effectiveBackup({ group: myGroupDoc, groups: directory.groups, supervisorUids }) : null;
  const nameOf = (uid) => directory.users.find((u) => u.uid === uid)?.name || 'otra supervisora';

  const [preview, setPreview] = useState(null);
  // Si la que está en el visor deja de estar pendiente (se aprobó o rechazó), se cierra.
  useEffect(() => {
    if (preview?.status === 'pending' && !pending.some((p) => p.id === preview.id)) setPreview(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  useEffect(() => {
    if (scoped && !mine.ids) return undefined;
    const col = firebase.firestore().collection('deletion_requests').where('status', '==', 'pending');
    const queries = scoped ? mine.ids.map((cid) => col.where('customerId', '==', cid)) : [col];
    return subscribeMany(
      queries,
      ({ docs, error }) => { setPendingError(error); setPending(docs.sort(byNewest('requestedAt'))); },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped, mine.ids]);

  // Resueltas en vivo (lo que resuelve otra supervisora aparece solo), de a
  // RESOLVED_PAGE con "Cargar más". Antes era una lectura única con tope de 100.
  const [resolvedLimit, setResolvedLimit] = useState(RESOLVED_PAGE);
  const [hasMoreResolved, setHasMoreResolved] = useState(false);
  useEffect(() => {
    if (scoped && !mine.ids) return undefined;
    const col = firebase.firestore().collection('deletion_requests');
    const queries = scoped
      ? mine.ids.map((cid) => col.where('customerId', '==', cid).orderBy('resolvedAt', 'desc').limit(resolvedLimit))
      : [col.orderBy('resolvedAt', 'desc').limit(resolvedLimit)];
    return subscribeMany(
      queries,
      ({ docs, error }) => {
        setResolvedError(error);
        const all = docs.sort(byNewest('resolvedAt'));
        // Con varias consultas se une y se corta; hay más si alguna llenó su tope.
        setHasMoreResolved(scoped ? all.length >= resolvedLimit : docs.length >= resolvedLimit);
        setResolved(all.slice(0, resolvedLimit)
          .filter((r) => r.status === 'approved' || r.status === 'rejected'));
      },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedLimit, scoped, mine.ids]);

  const approve = (r) => Modal.confirm({
    title: 'Aprobar y eliminar',
    content: `Se eliminará ${kindWithArticle(r.kind)} "${r.itemName}" y quedará en la papelera.`,
    okText: 'Aprobar y eliminar',
    okButtonProps: { danger: true },
    cancelText: 'Cancelar',
    onOk: async () => {
      setBusyId(r.id);
      try {
        const { outcome } = await dispatch(deletionRequests.approve({ id: r.id, roles: roles?.documents || {} }));
        notification.success({
          message: 'Solicitud aprobada',
          description: outcome === 'gone'
            ? 'El adjunto ya no estaba en el documento; no había nada que borrar.'
            : 'El adjunto se eliminó y quedó en la papelera.',
          placement: 'topRight',
        });
      } catch (e) {
        notification.error({ message: 'No se pudo aprobar', description: friendlyError(e, 'Intenta de nuevo.'), placement: 'topRight' });
      } finally {
        setBusyId(null);
      }
    },
  });

  // Aprobada que no alcanzó a borrar (needsExecution): se vuelve a ejecutar.
  const retry = async (r) => {
    setBusyId(r.id);
    try {
      const { outcome } = await dispatch(deletionRequests.retry({ id: r.id, roles: roles?.documents || {} }));
      notification.success({
        message: 'Eliminación completada',
        description: outcome === 'gone'
          ? 'El adjunto ya no estaba en el documento; no había nada que borrar.'
          : 'El adjunto se eliminó y quedó en la papelera.',
        placement: 'topRight',
      });
    } catch (e) {
      notification.error({ message: 'No se pudo eliminar', description: friendlyError(e, 'Intenta de nuevo.'), placement: 'topRight' });
    } finally {
      setBusyId(null);
    }
  };

  const reject = (r) => {
    let rejectReason = '';
    Modal.confirm({
      title: 'Rechazar solicitud',
      content: (
        <Input.TextArea rows={3} placeholder="Motivo del rechazo (opcional)" onChange={(e) => { rejectReason = e.target.value; }} />
      ),
      okText: 'Rechazar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setBusyId(r.id);
        try {
          await dispatch(deletionRequests.reject({ id: r.id, rejectReason }));
          notification.success({ message: 'Solicitud rechazada', placement: 'topRight' });
        } catch (e) {
          notification.error({ message: 'No se pudo rechazar', description: friendlyError(e, 'Intenta de nuevo.'), placement: 'topRight' });
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const common = [
    {
      title: 'Pide', key: 'who', render: (_, r) => (
        <Space direction="vertical" size={0}>
          {r.requestedByName || '—'}
          {!scoped && isUnassigned(r, directory.groups, supervisorUids) ? <Tag color="gold">Sin grupo</Tag> : null}
        </Space>
      ),
    },
    { title: 'Cliente', dataIndex: 'customerName', key: 'customer', render: (v) => v || '—' },
    { title: 'Documento', dataIndex: 'parentName', key: 'parent', render: (v) => v || '—' },
    {
      title: 'Qué', key: 'what', render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Tag>{KIND_LABELS[r.kind] || r.kind}</Tag>
          {/* Solo URLs de Storage en el href: la solicitud la escribe la
              auxiliar y la abre una supervisora (isSafeStorageUrl). */}
          {isSafeStorageUrl(r.itemUrl)
            ? <a href={r.itemUrl} target="_blank" rel="noopener noreferrer">{r.itemName}</a>
            : r.itemName}
          <Button size="small" onClick={() => setPreview(r)}>Previsualizar</Button>
        </Space>
      ),
    },
    { title: 'Motivo', dataIndex: 'reason', key: 'reason' },
    {
      title: 'Avisado a', key: 'notified', render: (_, r) => (
        <Space direction="vertical" size={0}>
          {(r.notifiedTo || []).length ? r.notifiedTo.map((n) => n.name).join(', ') : '—'}
          {r.reminderAt ? <Tag color="orange">Recordatorio enviado</Tag> : null}
        </Space>
      ),
    },
  ];

  const pendingColumns = [
    { title: 'Fecha', key: 'date', render: (_, r) => fmt(r.requestedAt) },
    ...common,
    {
      title: 'Acciones', key: 'actions', render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Button type="primary" danger size="small" loading={busyId === r.id} disabled={bulkRunning} onClick={() => approve(r)}>Aprobar</Button>
            <Button size="small" disabled={busyId === r.id || bulkRunning} onClick={() => reject(r)}>Rechazar</Button>
          </Space>
          {r.executionError
            ? <Typography.Text type="danger" style={{ fontSize: 12 }}>Falló al eliminar: {r.executionError}</Typography.Text>
            : null}
        </Space>
      ),
    },
  ];

  const resolvedColumns = [
    { title: 'Resuelta', key: 'resolvedAt', render: (_, r) => fmt(r.resolvedAt) },
    ...common,
    {
      title: 'Resultado', key: 'result', render: (_, r) => (
        <Space direction="vertical" size={0}>
          {needsExecution(r)
            ? <Tag color="orange">Aprobada, sin eliminar</Tag>
            : r.status === 'approved' ? <Tag color="green">Aprobada</Tag> : <Tag color="red">Rechazada</Tag>}
          {needsExecution(r)
            ? <Button size="small" loading={busyId === r.id} onClick={() => retry(r)}>Reintentar</Button>
            : null}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>por {r.resolvedByName || '—'}</Typography.Text>
          {r.rejectReason ? <Typography.Text style={{ fontSize: 12 }}>{r.rejectReason}</Typography.Text> : null}
          {r.status === 'approved' && r.executionError
            ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.executionError}</Typography.Text>
            : null}
        </Space>
      ),
    },
  ];

  const pendingShown = pending.filter(inScope);
  const resolvedShown = resolved.filter(inScope);
  const mineCount = pending.filter((r) => inMyGroup(r, members)).length;

  // La selección nunca incluye lo que no está en pantalla (cambió el filtro u
  // otra supervisora la resolvió): así no se aprueba nada que ella no vea.
  useEffect(() => {
    setSelectedKeys((keys) => keepVisible(keys, pendingShown));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, scope, directory.groups]);
  const selectedRows = pendingShown.filter((r) => selectedKeys.includes(r.id));
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    preserveSelectedRowKeys: true,
    onChange: setSelectedKeys,
    getCheckboxProps: () => ({ disabled: bulkRunning }),
    selections: [
      { key: 'all-visible', text: `Seleccionar las ${pendingShown.length} pendientes`, onSelect: () => setSelectedKeys(pendingShown.map((r) => r.id)) },
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  return (
    <>
      {myGroupDoc?.available === false ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`Estás marcada como no disponible. Tus avisos le llegan a ${myBackup ? nameOf(myBackup) : 'todas las demás supervisoras'}.`} />
      ) : null}
      {directory.error ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="No se pudo cargar toda la información de los grupos. El filtro Mi grupo puede verse incompleto." />
      ) : null}
      {pendingError || resolvedError || mine.error ? (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          message={scoped
            ? 'No se pudieron cargar todas las solicitudes de tus empresas. Se muestran las que sí cargaron.'
            : 'No se pudieron cargar las solicitudes.'}
          description={(pendingError || resolvedError || mine.error).message} />
      ) : null}
      {!scoped ? (
      <Segmented
        style={{ marginBottom: 12 }}
        value={scope || 'all'}
        onChange={setScope}
        options={[
          { value: 'mine', label: `Mi grupo (${mineCount})` },
          { value: 'all', label: `Todas (${pending.length})` },
        ]}
      />
      ) : null}
      <Tabs defaultActiveKey="pending" items={[
        {
          key: 'pending',
          label: `Pendientes (${pendingShown.length})`,
          children: (
            <>
              <BulkActions
                rows={pendingShown}
                selectedRows={selectedRows}
                onClearSelection={() => setSelectedKeys([])}
                onKeepSelected={setSelectedKeys}
                approveOne={(r) => dispatch(deletionRequests.approve({ id: r.id, roles: roles?.documents || {} }))}
                rejectOne={(r, rejectReason) => dispatch(deletionRequests.reject({ id: r.id, rejectReason }))}
                onRunningChange={setBulkRunning}
              />
              <Table rowKey="id" size="middle" bordered columns={pendingColumns} dataSource={pendingShown}
            rowSelection={rowSelection}
            locale={{ emptyText: scope === 'mine' ? 'No hay solicitudes pendientes de tu grupo' : 'No hay solicitudes pendientes' }}
            pagination={{ defaultPageSize: 10 }} scroll={{ x: true }} />
            </>
          ),
        },
        {
          key: 'resolved',
          label: 'Resueltas',
          children: <Table rowKey="id" size="middle" bordered columns={resolvedColumns} dataSource={resolvedShown}
            locale={{ emptyText: 'Todavía no hay solicitudes resueltas' }} pagination={{ defaultPageSize: 10 }} scroll={{ x: true }}
            footer={hasMoreResolved ? () => (
              <Button onClick={() => setResolvedLimit((n) => n + RESOLVED_PAGE)}>Cargar más</Button>
            ) : undefined} />,
        },
        ...(!scoped ? [{
          key: 'group',
          label: 'Mi grupo',
          children: directory.loading ? null : <MyGroup directory={directory} myUid={myUid} isRoot={isRootUser} />,
        }] : []),
      ]} />
      <PreviewDrawer
        open={!!preview}
        request={preview}
        onClose={() => setPreview(null)}
        onApprove={approve}
        onReject={reject}
        busy={busyId === preview?.id || bulkRunning}
      />
    </>
  );
};
